import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactUpdatePublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/public/contact-update/${token}`)
      .then(r => {
        setData(r.data);
        setForm(r.data.current);
      })
      .catch(e => setErr(e.response?.data?.detail || "Lien invalide ou expiré"));
  }, [token]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const { data: res } = await axios.post(`${API}/api/public/contact-update/${token}`, form);
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSaving(false);
    }
  };

  if (err) return (
    <div className="min-h-screen bg-zinc-950 text-zinc-200 flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <div className="text-4xl mb-3">⛓️</div>
        <div className="text-lg text-red-300">{err}</div>
        <div className="text-xs text-zinc-500 mt-3">Contacte Kru Ralph pour un nouveau lien.</div>
      </div>
    </div>
  );

  if (done) return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-zinc-900 to-black text-zinc-100 flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <div className="text-5xl mb-4">✅</div>
        <h1 className="text-2xl font-bold text-amber-400 mb-2">Merci !</h1>
        <div className="text-zinc-300">Tes coordonnées ont été mises à jour.</div>
        <div className="text-xs text-zinc-500 mt-4">Tu vas recevoir ton Carnet de Progression sous peu.</div>
        <div className="mt-8 text-[10px] text-zinc-600">© Institut Impact Élite Martial · Kru Ralph D. Jean-Pierre</div>
      </div>
    </div>
  );

  if (!data) return <div className="min-h-screen bg-zinc-950 text-zinc-400 flex items-center justify-center">Chargement…</div>;

  const { student, current } = data;
  const isMinor = student.is_minor;

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-zinc-900 to-black text-zinc-100" data-testid="contact-update-form">
      {/* Header */}
      <div className="px-5 pt-8 pb-5 border-b border-amber-900/30 bg-gradient-to-r from-amber-950/20 to-zinc-900/20">
        <div className="text-xs uppercase tracking-widest text-amber-500/80">Institut Impact Élite Martial</div>
        <h1 className="text-2xl font-bold text-amber-400 mt-1">Mets à jour tes coordonnées</h1>
        <div className="text-sm text-zinc-400 mt-2">Élève : <b className="text-zinc-100">{student.name}</b></div>
      </div>

      <div className="px-5 py-6 max-w-xl mx-auto space-y-5">
        {isMinor && (
          <div className="bg-blue-950/40 border border-blue-700 rounded-lg p-3 text-sm text-blue-200">
            👶 Mineur détecté ({student.age} ans) — Privilégie les champs "parent" ci-dessous.
          </div>
        )}

        {/* Parent info (if minor) */}
        {isMinor && (
          <>
            <Field label="Nom du parent" value={form.parent_name} onChange={v => update("parent_name", v)} testid="parent-name" />
            <Field label="Téléphone du parent" type="tel" value={form.parent_phone} onChange={v => update("parent_phone", v)} placeholder="819-555-1234" testid="parent-phone" />
            <Field label="Email du parent" type="email" value={form.parent_email} onChange={v => update("parent_email", v)} testid="parent-email" />
          </>
        )}

        {/* Adult / general info */}
        {!isMinor && (
          <>
            <Field label="Téléphone" type="tel" value={form.phone} onChange={v => update("phone", v)} placeholder="819-555-1234" testid="phone" />
            <Field label="Email" type="email" value={form.email} onChange={v => update("email", v)} testid="email" />
          </>
        )}

        <Field label="Adresse complète" value={form.address} onChange={v => update("address", v)} placeholder="123 rue X, Val-d'Or, QC J9P 0X0" testid="address" />

        {/* Optional secondary contact even for adults */}
        {!isMinor && (
          <details className="text-sm">
            <summary className="cursor-pointer text-amber-400 hover:text-amber-300 select-none">+ Ajouter un contact d'urgence (optionnel)</summary>
            <div className="mt-3 space-y-3">
              <Field label="Nom du contact" value={form.parent_name} onChange={v => update("parent_name", v)} testid="emergency-name" />
              <Field label="Tél. contact d'urgence" type="tel" value={form.parent_phone} onChange={v => update("parent_phone", v)} testid="emergency-phone" />
              <Field label="Email contact" type="email" value={form.parent_email} onChange={v => update("parent_email", v)} testid="emergency-email" />
            </div>
          </details>
        )}

        <button
          data-testid="submit-btn"
          onClick={submit}
          disabled={saving}
          className="w-full bg-amber-600 hover:bg-amber-500 disabled:bg-zinc-700 text-zinc-950 font-semibold py-3 rounded-lg transition mt-4"
        >
          {saving ? "Envoi…" : "Sauvegarder"}
        </button>

        <div className="text-[10px] text-zinc-600 text-center pt-6 border-t border-zinc-900">
          🔒 Lien sécurisé personnel · Tes infos restent privées · Validité 90 jours
        </div>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-zinc-500 block mb-1">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2.5 text-sm focus:border-amber-500 focus:outline-none transition"
      />
    </div>
  );
}
